import axios, { AxiosResponse } from 'axios';
import { Authenticator } from '../auth';
import LocalCache from './LocalCache';

const axiosInstance = axios.create();

interface ApiResponse<T = any> {
  status: boolean;
  message?: string;
  error?: string;
  data: T;
}

enum ApiMethod {
  Get,
  Post,
  Put,
  Patch,
  Delete,
}

axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  /** Interceptor for handling response errors. Refreshes token on 401 error and retries the request. */
  async (error) => {
    const originalRequest = error.config;
    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const authenticator = new Authenticator();
      const token = await authenticator.refreshToken();
      originalRequest.headers.Authorization = `Bearer ${token}`;
      return axiosInstance(originalRequest);
    }
    return Promise.reject(error);
  },
);

/** API handler class for making HTTP requests with authentication and error handling. */
export class Api {
  static isRefreshingToken = false;

  /** Executes an HTTP method using Axios. */
  private async executeMethod<DataType>(
    method: ApiMethod,
    url: string,
    params?: any,
    data?: any,
    contentType = 'application/json',
  ) {
    try {
      let response: AxiosResponse<ApiResponse<DataType>>;
      const config = {
        headers: {
          Authorization: 'Bearer ' + LocalCache.fetchToken(),
          'Content-Type': contentType,
        },
        params: params,
      };

      switch (method) {
        case ApiMethod.Post:
          response = await axiosInstance.post<ApiResponse<DataType>>(url, data, config);
          break;
        case ApiMethod.Put:
          response = await axiosInstance.put<ApiResponse<DataType>>(url, data, config);
          break;
        case ApiMethod.Patch:
          response = await axiosInstance.patch<ApiResponse<DataType>>(url, data, config);
          break;
        case ApiMethod.Delete:
          response = await axiosInstance.delete<ApiResponse<DataType>>(url, config);
          break;
        default:
          response = await axiosInstance.get<ApiResponse<DataType>>(url, config);
          break;
      }

      return response.data.data;
    } catch (error: any) {
      if (error.response?.data) {
        const { message } = error.response.data;
        if (Array.isArray(message)) {
          throw message.join('|');
        }

        throw message;
      }

      throw 'Unable to process request.';
    }
  }

  /** Creates a resource using HTTP POST. */
  async createResource<DataType>(url: string, data: any = {}): Promise<DataType> {
    return await this.executeMethod(ApiMethod.Post, url, undefined, data);
  }

  /** Fetches a resource using HTTP POST (used when GET is not supported). */
  async getResourceFromPostMethod<DataType>(url: string, data: any = {}): Promise<DataType> {
    return await this.executeMethod(ApiMethod.Post, url, undefined, data);
  }

  /** Updates a resource using HTTP PUT. */
  async updateResource<DataType>(url: string, data: any): Promise<DataType> {
    return await this.executeMethod(ApiMethod.Put, url, undefined, data);
  }

  /** Partially updates a resource using HTTP PATCH. */
  async updatePartialResource<DataType>(url: string, data: any): Promise<DataType> {
    return await this.executeMethod(ApiMethod.Patch, url, undefined, data);
  }

  /** Deletes a resource using HTTP DELETE. */
  async deleteResource<DataType>(url: string): Promise<DataType> {
    return await this.executeMethod(ApiMethod.Delete, url);
  }

  /** Fetches a resource using HTTP GET. */
  async getResource<DataType>(url: string, params?: Record<string, unknown>): Promise<DataType> {
    return await this.executeMethod(ApiMethod.Get, url, params);
  }

  /** Submits form data using HTTP POST with multipart/form-data. */
  async postFormData(url: string, data: { key: string; value: any }[]) {
    const form = new FormData();
    data.forEach((pair) => {
      form.append(pair.key, pair.value);
    });
    return await this.executeMethod(ApiMethod.Post, url, undefined, form, 'multipart/form-data');
  }

  /** Submits multiple files using HTTP POST with multipart/form-data. */
  async postMultiFilesFormData(url: string, data: any) {
    return await this.executeMethod(ApiMethod.Post, url, undefined, data, 'multipart/form-data');
  }

  /** Submits form data using HTTP PUT with multipart/form-data. */
  async putFormData(url: string, data: { key: string; value: any }[]) {
    const form = new FormData();
    data.forEach((pair) => {
      form.append(pair.key, pair.value);
    });
    return await this.executeMethod(ApiMethod.Put, url, undefined, form, 'multipart/form-data');
  }

  /** Downloads a file from the given URL as a Blob. */
  async getFile(url: string): Promise<Blob> {
    const response = await axiosInstance.get(url, {
      responseType: 'blob',
      headers: {
        Authorization: 'Bearer ' + LocalCache.fetchToken(),
      },
    });

    return new Blob([response.data]);
  }
}
